import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useMutation } from '@apollo/react-hooks';
import classNames from 'classnames';

import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import StarBorderRoundedIcon from '@material-ui/icons/StarBorderRounded';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListSubheader from '@material-ui/core/ListSubheader';
import ListItem from '@material-ui/core/ListItem';

import ConfirmContext from 'contexts/ConfirmContext';

import {
  BookedAddOns,
  BookedMandatoryFees,
  getAccommodationBookingSchedule,
  getActivityBookingSchedule,
  guestsSumSeats,
  guestsDetailsInformation,
  isParticipantInfo
} from 'utils/bookings/prepareBookingsData';

import BookingPriceDetailsPanel from '../booking-price-total/BookingPriceDetails';

import { CANCEL_ACCOMMODATION_BOOKING } from 'graphql/mutations/booking';

import useStyles from './styles';
import NotesForm from './NotesForm';
import EditIcon from '@material-ui/icons/Edit';

const ACCOMMODATION_TYPE = 'Accommodation';
const ACTIVITY_TYPE = 'Activity';

const CancelledInfo = ({ cancellationTime, isHandled, total }) => {
  const classes = useStyles();
  const [t] = useTranslation();

  return (
    <List className={classes.root}>
      <ListSubheader className={classes.subheader}>
        {t('Cancelled date')}
      </ListSubheader>
      <ListItem className={classes.listItem}>
        {`${moment(cancellationTime).format('DD-MM-YYYY')}, `}
        {moment(cancellationTime).format('HH:mm')}
      </ListItem>
      <ListSubheader className={classes.subheader}>{t('Status')}</ListSubheader>
      <ListItem className={classes.listItem}>
        {isHandled && t('Handled')}
        {!isHandled && t('Not handled')}
      </ListItem>
      <BookingPriceDetailsPanel priceDetails={total} />
    </List>
  );
};

const BookingsDetailsDialog = ({
  open,
  booking,
  onClose,
  bookingTotal,
  isEditCancelBtns,
  cancelBooking,
  refetchParams
}) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const history = useHistory();
  const confirm = React.useContext(ConfirmContext);
  const { enqueueSnackbar } = useSnackbar();

  const [isNotesEditMode, setNotesEditMode] = useState(false);
  const [presetNotes, setPresetNotes] = useState(
    booking.bookingNotes || booking.activityNotes
  );
     
  useEffect(() => {
    setPresetNotes(booking.bookingNotes || booking.activityNotes);
  }, [booking]);

  const [cancelAccommodationBooking] = useMutation(
    CANCEL_ACCOMMODATION_BOOKING
  );

  if (Object.keys(booking).length === 0) {
    return null;
  }

  const accommodationCancellationHandler = item => {
    confirm({
      title: `${t('Cancel Booking')}?`,
      warningText: `${
        item.relatedActivitiesBookings.length && !item.isRelated
          ? t('Please note that related booking also will be deleted')
          : ''
      }`,
      description: `${item.fromDate} — ${item.toDate} • ${item.guestFirstName} ${item.guestLastName} • ${item.accommodation.title}`,
      confirmationText: t('Cancel Booking'),
      cancellationText: t('Back')
    }).then(() => {
      cancelAccommodationBooking({ variables: { id: item.id } }).then(() => {
        enqueueSnackbar(t('Accommodation booking was successfully canceled!'), {
          variant: 'success'
        });
        cancelBooking();
      });
    });
  };

  return (
    <Dialog open={open} classes={{ paper: classes.paper }}>
      <IconButton className={classes.closeBtn} onClick={onClose}>
        <HighlightOffIcon className={classes.closeIcon} />
      </IconButton>

      {booking && (
        <DialogTitle
          id="alert-dialog-title"
          classes={{ root: classes.title }}
          disableTypography={true}
        >
          {booking.trekksoftId || booking.expediaReferenceNumber
            ? booking.trekksoftId || booking.expediaReferenceNumber
            : booking.referenceNumber}{' '}
          • {booking.guestFirstName} {booking.guestLastName}
        </DialogTitle>
      )}

      <DialogTitle
        id="alert-dialog-title"
        classes={{ root: classes.unitTitle }}
        disableTypography={true}
      >
        {booking.bookingType === ACTIVITY_TYPE && (
          <StarBorderRoundedIcon
            className={classes.unitIcon}
          ></StarBorderRoundedIcon>
        )}

        {booking.bookingType === ACCOMMODATION_TYPE && (
          <HomeOutlinedIcon className={classes.unitIcon}></HomeOutlinedIcon>
        )}

        {booking.bookingType === ACTIVITY_TYPE && booking.activity.title}

        {booking.bookingType === ACCOMMODATION_TYPE &&
          booking.accommodation.title}
      </DialogTitle>

      <DialogContent classes={{ root: classes.contentRoot }}>
        <List className={classes.root}>
          <ListSubheader className={classes.subheader}>
            {t('Number of guests')}
          </ListSubheader>

          <ListItem className={classes.listItem}>
            {guestsSumSeats(booking.guests)}

            {isParticipantInfo(booking.guests) &&
              ` (${guestsDetailsInformation(booking.guests, t)})`}
          </ListItem>

          <ListSubheader className={classes.subheader}>
            {t('Checkin - checkout')}
          </ListSubheader>

          <ListItem className={classes.listItem}>
            {booking.bookingType === ACCOMMODATION_TYPE &&
              getAccommodationBookingSchedule(booking)}

            {booking.bookingType === ACTIVITY_TYPE &&
              getActivityBookingSchedule(booking)}
          </ListItem>

          <ListSubheader className={classes.subheader}>
            {t('Platform')}
          </ListSubheader>

          <ListItem className={classes.listItem}>{booking.platform}</ListItem>

          <ListSubheader className={classes.subheader}>
            {t('Add-ons')}
          </ListSubheader>

          <ListItem className={classes.listItem}>
            {BookedAddOns(booking.bookedAddOns)}
          </ListItem>

          <ListSubheader className={classes.subheader}>
            {t('Mandatory-Fees')}
          </ListSubheader>

          <ListItem className={classes.listItem}>
            {BookedMandatoryFees(booking.bookedMandatoryFees)}
          </ListItem>

          <ListSubheader className={classes.subheader}>
            {t('First name')}
          </ListSubheader>
          <ListItem className={classes.listItem}>
            {booking.guestFirstName}
          </ListItem>

          <ListSubheader className={classes.subheader}>
            {t('Last name')}
          </ListSubheader>

          <ListItem className={classes.listItem}>
            {booking.guestLastName}
          </ListItem>

          <ListSubheader className={classes.subheader}>
            {t('Phone')}
          </ListSubheader>

          <ListItem className={classes.listItem}>
            {booking.guestPhoneNumber}
          </ListItem>

          <ListSubheader className={classes.subheader}>
            {t('Email')}
          </ListSubheader>

          <ListItem className={classes.listItem}>{booking.guestEmail}</ListItem>

          <ListSubheader className={classes.subheader}>
            {t('Booking date')}
          </ListSubheader>

          <ListItem
            className={classes.listItem}
            style={!booking.isCanceled ? { borderBottom: 'None' } : {}}
          >
            {booking.creationTime}
          </ListItem>

          {!booking.isCanceled && bookingTotal && (
            <BookingPriceDetailsPanel priceDetails={bookingTotal} />
          )}

          <ListSubheader
            className={classNames(classes.subheader, classes.notesLabel)}
          >
            <div>{t('Notes')}</div>

            {!isNotesEditMode && !!presetNotes && (
              <IconButton
                onClick={() => setNotesEditMode(true)}
                style={{ padding: '4px' }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </ListSubheader>

          <ListItem
            className={classes.listItem}
            style={{ borderBottom: 'None' }}
          >
            {isNotesEditMode || !presetNotes ? (
              <NotesForm
                id={booking.id}
                accommodationId={booking?.accommodation?.id}
                defaultValue={presetNotes}
                onCancel={() => setNotesEditMode(false)}
                onConfirm={newNote => setPresetNotes(newNote)}
                refetchParams={refetchParams}
                isActivity={booking.__typename === 'ActivityBookingType'}
              />
            ) : (
              presetNotes
            )}
          </ListItem>
        </List>

        {booking.isCanceled && bookingTotal && (
          <CancelledInfo
            isHandled={booking.isHandled}
            cancellationTime={booking.cancellationTime}
            total={bookingTotal}
          />
        )}
      </DialogContent>

      <DialogActions classes={{ root: classes.actionsRoot }}>
        {isEditCancelBtns && (
          <div>
            <Button
              variant="outlined"
              onClick={e => {
                e.stopPropagation();
                history.push(`/accommodations-bookings/${booking.id}/edit`);
              }}
              classes={{ root: classes.actionBtnRoot }}
            >
              {t('Edit')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => accommodationCancellationHandler(booking)}
              classes={{ root: classes.cancelBtnRoot }}
            >
              {t('Cancel Accommodation')}
            </Button>
          </div>
        )}
        <Button
          variant="outlined"
          onClick={onClose}
          classes={{ root: classes.cancelBtnRoot }}
        >
          {t('Back')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingsDetailsDialog;
